const KNOWN_IMAGE_BUCKETS = [
  "artstyle-refs",
  "chapter-images",
  "entity-images",
  "generated-images",
  "lore-covers",
  "project-covers",
] as const;

export type KnownImageBucket = (typeof KNOWN_IMAGE_BUCKETS)[number];

export interface ParsedAssetRef {
  bucket: KnownImageBucket;
  isQualified: boolean;
  path: string;
  raw: string;
}

function normalizeSegments(value: string) {
  return value
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean);
}

export function isKnownImageBucket(value: string): value is KnownImageBucket {
  return (KNOWN_IMAGE_BUCKETS as readonly string[]).includes(value);
}

export function parseBucketQualifiedAssetRef(
  assetRef: string | null | undefined,
): ParsedAssetRef | null {
  if (!assetRef) {
    return null;
  }

  const normalized = assetRef.trim();
  if (!normalized) {
    return null;
  }

  const segments = normalizeSegments(normalized);
  const [bucket, ...pathSegments] = segments;

  if (!bucket || !isKnownImageBucket(bucket) || pathSegments.length === 0) {
    return null;
  }

  return {
    bucket,
    isQualified: true,
    path: pathSegments.join("/"),
    raw: normalized,
  };
}

export function resolveStoredAssetRef(
  assetRef: string | null | undefined,
  fallbackBucket: KnownImageBucket,
): ParsedAssetRef | null {
  if (!assetRef) {
    return null;
  }

  const qualified = parseBucketQualifiedAssetRef(assetRef);
  if (qualified) {
    return qualified;
  }

  const normalized = normalizeSegments(assetRef).join("/");
  if (!normalized) {
    return null;
  }

  return {
    bucket: fallbackBucket,
    isQualified: false,
    path: normalized,
    raw: normalized,
  };
}

export function buildBucketQualifiedAssetRef(bucket: KnownImageBucket, path: string) {
  const normalizedPath = normalizeSegments(path).join("/");
  return normalizedPath ? `${bucket}/${normalizedPath}` : bucket;
}

export function getAssetRefFileName(assetRef: string | null | undefined) {
  const resolved = resolveStoredAssetRef(assetRef, "generated-images");
  if (!resolved) {
    return "";
  }

  return resolved.path.split("/").at(-1) ?? resolved.path;
}

export function getDisplayLabelFromAssetRef(assetRef: string | null | undefined) {
  const fileName = getAssetRefFileName(assetRef);
  const extensionIndex = fileName.lastIndexOf(".");
  const baseName = extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName;

  return decodeURIComponent(baseName)
    .replace(
      /^([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}|ref-[0-9]+-[a-z0-9]+)-/i,
      "",
    )
    .replace(/[_-]+/g, " ")
    .trim();
}
